import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  styled,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateAnamnesisImagingDiagnosis } from '../../../../../api/generated/anamnesis-controller/anamnesis-controller.ts';
import { ImagingDiagnosisDtoImagingProcedure } from '../../../../../api/generated/model';
import { getGetAnamnesesByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FileInput } from '../../../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { getImagingProcedureTypenName } from '../../../../../utils/anamnesis.ts';
import { blobToBase64 } from '../../../../../utils/file.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';

interface FormValues {
  imagingProcedure: ImagingDiagnosisDtoImagingProcedure;
  date: string;
  file: File | null;
  imagingFinding: string;
}

export function AddImagingDiagnosisDialog({
  open,
  onClose,
  anamnesisId,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  anamnesisId: string;
  patientId: string;
}) {
  const queryClient = useQueryClient();

  const {
    reset,
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      imagingProcedure: undefined,
      date: '',
      file: null,
      imagingFinding: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const createImagingDiagnosisMutation = useCreateAnamnesisImagingDiagnosis({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetAnamnesesByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Hinzufügen der Bildgebenden Diagnose ist ein Fehler aufgetreten');
      },
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 600 }}>
        <ModalClose />
        <Typography level={'h4'}>Bildgebende Diagnose hinzufügen</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit(async (data) => {
            const imagingDiagnosisData = {
              imagingProcedure: data.imagingProcedure,
              date: data.date,
              ...(data.file != null
                ? {
                    file: {
                      fileName: data.file.name,
                      file: await blobToBase64(data.file),
                    },
                  }
                : {}),
              imagingFinding: getStringOrUndefined(data.imagingFinding),
            };

            createImagingDiagnosisMutation.mutate({
              id: anamnesisId,
              data: imagingDiagnosisData as never,
            });
          })}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.imagingProcedure}>
                <FormLabel>
                  <Typography>Bildgebendes Verfahren *</Typography>
                </FormLabel>
                <Controller
                  name={'imagingProcedure'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange } }) => (
                    <Select onChange={(_, value) => onChange(value)}>
                      {Object.values(ImagingDiagnosisDtoImagingProcedure).map((type) => (
                        <Option value={type} key={type}>
                          {getImagingProcedureTypenName(type)}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
                {errors.imagingProcedure && (
                  <FormHelperText>Bildgebendes Verfahren muss angegeben werden</FormHelperText>
                )}
              </FormControl>
              <FormControl error={!!errors.date}>
                <FormLabel>
                  <Typography>Datum</Typography>
                </FormLabel>
                <GermanDateInput control={control} name="date" />
              </FormControl>
              <FormControl error={!!errors.file} sx={{ gridColumn: '1 / span 2' }}>
                <FormLabel>
                  <Typography>Datei</Typography>
                </FormLabel>
                <Controller
                  name={'file'}
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FileInput
                      value={value}
                      onChange={onChange}
                      accept={'application/pdf'}
                      placeholder={'Datei hochladen'}
                    />
                  )}
                />
              </FormControl>
              <FormControl sx={{ gridColumn: '1 / span 2' }}>
                <FormLabel>
                  <Typography>Befund</Typography>
                </FormLabel>
                <Textarea {...register('imagingFinding')} minRows={4} maxRows={4} />
              </FormControl>
            </FormGrid>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createImagingDiagnosisMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
